import React, { useMemo, useState } from 'react';
import {
  OneWayInOpportunitySimpleV2Dto,
  OneWayInOpportunityStatusCreateDto,
  OneWayInRecommendation,
  OneWayInStatus,
  ServiceRecommendationDtoV2,
} from 'src/api/v2';

import { useUser } from 'src/features/authorization';
import { useUsers } from 'src/features/users';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Checkbox, RichTextEditor, Alert } from '@innovationnorway/cl-react';
import useOneWayInServices from '../../hooks/use-one-way-in-services';
import { useCluster } from 'src/features/cluster';
import getInitialMessageHtml from '../../utils/getInitialMessageHtml';
import { sanitizeD365RichTextEditor } from '../../utils/htmlTextConverter';
import { ErrorMessages } from '../constants';
import { Link } from 'react-router-dom';
import { SERVICES } from 'src/features/services';
import { handleSelectedServicesChange } from '../helpers';
import useServiceTemplatesList from '../../hooks/use-service-templates';
import ToastContent from 'src/components/ToastContent/ToastContent';
import { toast } from 'react-toastify';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import useOneWayInMultiple from '../../hooks/use-one-way-in-multiple';
import useOneWayIn from '../../hooks/use-one-way-in';
import { parseDateString } from 'src/utils/date';
import { useLocalStorage } from 'usehooks-ts';

interface RecommendServicesModalProps {
  dialogOpen: boolean;
  onClose: () => void;
  opportunities: OneWayInOpportunitySimpleV2Dto[];
  initialSelectedServiceIds?: string[];
}

const RecommendServicesModal = ({
  dialogOpen,
  onClose,
  opportunities,
  initialSelectedServiceIds: selectedServiceIdsProp,
}: RecommendServicesModalProps) => {
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<ErrorMessages | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>(selectedServiceIdsProp || []);
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { cluster } = useCluster();
  const { activeClusterServices, isLoading } = useOneWayInServices(cluster?.id);
  const { user, displayName, isAadUser } = useUser();
  const { users } = useUsers();
  const { allServiceTemplates } = useServiceTemplatesList();
  const { oneWayInGroupedFormsWOEnrollmentsQuery, recommendService, recommendServiceBatch, formsLoading } =
    useOneWayInMultiple(
      opportunities.map((r) => r.oneWayInId),
      dialogOpen,
    );
  const siteimprove = useSiteimprove();
  const { createOpportunity } = useOneWayIn();
  const [board] = useLocalStorage(`opportunities__tableView`, true);

  const forms = useMemo(() => {
    return oneWayInGroupedFormsWOEnrollmentsQuery.map((query) => query.data);
  }, [oneWayInGroupedFormsWOEnrollmentsQuery]);

  const firstTemplate = useMemo(() => {
    return allServiceTemplates ? allServiceTemplates[0] : undefined;
  }, [allServiceTemplates]);

  const userEmail = useMemo(
    () => users?.find((u) => u.id === user?.clusterUserId)?.emailAddress,
    [users, user],
  );

  const userPhone = useMemo(() => users?.find((u) => u.id === user?.clusterUserId)?.phone, [users, user]);

  const initialValue = useMemo(
    () =>
      getInitialMessageHtml(
        activeClusterServices,
        selectedServiceIds,
        opportunities.length === 1 ? opportunities[0].customerName : undefined,
        userEmail,
        userPhone,
        displayName,
        cluster?.name,
      ),
    [
      activeClusterServices,
      selectedServiceIds,
      opportunities,
      userEmail,
      userPhone,
      displayName,
      cluster?.name,
    ],
  );

  const mailContent = useMemo(
    () => sanitizeD365RichTextEditor(message || initialValue),
    [initialValue, message],
  );

  const handleRecommendService = async () => {
    if (selectedServiceIds.length === 0) {
      setErrorMessage(ErrorMessages.NoServicesSelected);
      return;
    }

    if (mailContent === '' || mailContent === '<p><br></p>') {
      setErrorMessage(ErrorMessages.FieldEmpty);
      return;
    }

    const recommendations = opportunities.map(async (opportunity) => {
      const form = forms.find((f) => f?.id === opportunity.oneWayInId);

      // Create opportunity if it does not exist
      if (!opportunity.clusterId && !!cluster && !!form) {
        const createDto = {
          clusterId: cluster.id,
          oneWayInId: form.id!,
          title: form.tittel ?? '',
          municipality: form.prosjektkommune ?? '',
          customerName: form.kontakt?.navn,
          organizationName: form.organisasjon?.navn,
          organizationNumber: form.organisasjon?.orgNr,
          submittedOn: form.innsendtDato ? parseDateString(form.innsendtDato) : undefined,
        };
        const created = await createOpportunity.mutateAsync(createDto);
        if (created) {
          opportunity.id = created.id;
        }
      }

      const opportunityStatusUpdate = {
        oneWayInOpportunityId: opportunity.id,
        referenceClusterId: undefined,
        status: OneWayInStatus.Active,
        description: undefined,
        createdOn: new Date(),
        closedReason: undefined,
        recommendedServices: selectedServiceIds || [],
        registeredServices: [],
        clusterContactId: undefined,
        clusterUserId: isAadUser ? undefined : user?.clusterUserId,
        handlersName: displayName || undefined,
        sendFollowUpReminderOn: undefined,
        admittedToProgram: false,
        sivaPhase: undefined,
        deliveredOn: undefined,
      } as OneWayInOpportunityStatusCreateDto;

      const serviceRecommendation: ServiceRecommendationDtoV2 = {
        recipientEmail: form?.kontakt?.epost ?? '',
        recipientName: form?.kontakt?.navn ?? '',
        comment: mailContent,
        senderName: displayName ?? '',
        senderOrganisation: cluster?.name || '',
        sendToActor: false,
        sendToCustomer: !!form?.kontakt,
      };

      return {
        recommendation: {
          opportunityStatusCreate: opportunityStatusUpdate,
          serviceRecommendation: serviceRecommendation,
        } as OneWayInRecommendation,
        formId: opportunity.oneWayInId ?? '',
      };
    });

    recommendServiceBatch(await Promise.all(recommendations))
      .then(() => {
        if (board) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.SEND_RECOMMENDATION,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.SUCCESS,
          );
        } else {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.actions.SEND_RECOMMENDATION,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.labels.SUCCESS,
          );
        }
        handleCloseModal();
        toast(
          <ToastContent
            title={tOneWayIn('customerFollowUp.recommendServices.toast.success.title')}
            text={tOneWayIn('toasts.activityLogUpdated')}
            success={true}
          />,
          {
            toastId: 'ServicesRecommended',
            autoClose: 1000 * 15,
            position: 'bottom-center',
          },
        );
      })
      .catch((e) => {
        if (board) {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.SEND_RECOMMENDATION,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.labels.ERROR,
          );
        } else {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.id,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.actions.SEND_RECOMMENDATION,
            SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.labels.ERROR,
          );
        }
        console.error(e);
        setErrorMessage(ErrorMessages.SomethingWentWrong);
      });
  };

  const handleCloseModal = () => {
    setMessage('');
    setErrorMessage(null);
    setSelectedServiceIds([]);
    onClose();
  };

  const dialogContentNoServices = (
    <>
      <div className="stack-small padding-x--3 padding-bottom--1">
        <span className="dialog-with-sidebar--heading recommend-services-modal--header">
          {tOneWayIn('opportunityBoard.noServicesModal.title')}
        </span>
        <p className="font-size--medium">{tOneWayIn('opportunityBoard.noServicesModal.description')}</p>
      </div>
      <div className="dialog-with-sidebar--action-row">
        <Link to={firstTemplate ? `${SERVICES.BASE}/serviceTemplate/${firstTemplate?.id}` : SERVICES.BASE}>
          <Button>{tOneWayIn('button.addService')}</Button>
        </Link>
        <Button variant="secondary" onClick={handleCloseModal}>
          {tCommon('cancel')}
        </Button>
      </div>
    </>
  );

  const dialogContentWithServices = (
    <>
      <div className="stack-small padding-x--3 padding-bottom--1">
        <span className="dialog-with-sidebar--heading recommend-services-modal--header">
          {tOneWayIn('opportunityBoard.recommendServicesModal.title')}
        </span>
        <p className="font-size--medium">
          {tOneWayIn('opportunityBoard.recommendServicesModal.description')}
        </p>
      </div>
      <div className="dialog-with-sidebar--container recommend-services-modal--container display--flex flex-direction--row gap--2 padding--3 with-sidebar">
        <div className="recommend-services-modal--sidebar dialog-with-sidebar--side-bar font-size--medium">
          {activeClusterServices?.map((service) => (
            <div key={service.id}>
              <Checkbox
                label={service.name}
                checked={selectedServiceIds.includes(service.id)}
                onChange={(e) =>
                  setSelectedServiceIds(
                    handleSelectedServicesChange(service.id, e.target.checked, selectedServiceIds),
                  )
                }
              />
            </div>
          ))}
        </div>
        <div className="stack-medium padding--2 border-radius--1 background-color--white display--flex flex-direction--column flex--1 recommend-services-modal--message-editor">
          <span className="font-weight--bold font-size--large">{`${tCommon('subject')}: Anbefalte tjenester hos ${cluster?.name}`}</span>
          {!isLoading && (
            <RichTextEditor
              key={initialValue}
              rows={8}
              initialValue={initialValue}
              onChange={(value) => setMessage(value)}
            />
          )}
          <span className="font-size--medium">MERK: Svar til denne e-postadressen blir ikke behandlet.</span>
        </div>
      </div>
      {errorMessage && (
        <div className="dialog-with-sidebar--alert--container">
          <Alert data-color="danger" className="dialog-with-sidebar--alert">
            {tOneWayIn(`errorMessage.${errorMessage}`)}
          </Alert>
        </div>
      )}
      <div className="dialog-with-sidebar--action-row">
        <Button
          onClick={handleRecommendService}
          loading={recommendService.isLoading}
          disabled={recommendService.isLoading || formsLoading}
        >
          {tOneWayIn('customerFollowUp.recommendServices.modal.button.primary')}
        </Button>
        <Button variant="secondary" onClick={handleCloseModal}>
          {tCommon('cancel')}
        </Button>
      </div>
    </>
  );

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleCloseModal}
      className="dialog-with-sidebar recommend-services-modal"
    >
      {activeClusterServices.length === 0 && !isLoading ? dialogContentNoServices : dialogContentWithServices}
    </Dialog>
  );
};

export default RecommendServicesModal;
